// client/src/components/TrialBanner.js
"use client";

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Clock, Zap, CreditCard, X } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { getAccessToken } from '../utils/accessToken';
import Cookies from 'js-cookie';
import { SIGNUP_TRIAL_DAYS } from '@/lib/constants';

// variant="page" (default) sits under the navbar on sm+ screens only;
// variant="menu" is the mobile-menu copy, which replaces it on small screens.
/** @param {{ variant?: 'page' | 'menu', onNavigate?: () => void }} props */
const TrialBanner = ({ variant = 'page', onNavigate }) => {
  const inMenu = variant === 'menu';
  const router = useRouter();
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchTrialStatus();
  }, []);

  const fetchTrialStatus = async () => {
    try {
      const response = await apiRequest('GET', '/trial/status');
      setTrialStatus(response);
    } catch (error) {
      console.error('Error fetching trial status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('trialBannerDismissed', Date.now().toString());
  };

  const handleUpgrade = () => {
    onNavigate?.();
    router.push('/subscription');
  };

  const outerClass = inMenu ? 'py-3' : 'hidden sm:block py-4';
  const innerClass = inMenu ? 'px-4 flex items-center' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center';

  useEffect(() => {
    const dismissedAt = localStorage.getItem('trialBannerDismissed');
    if (dismissedAt) {
      const hoursSinceDismissal = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursSinceDismissal < 24) {
        setDismissed(true);
      }
    }
  }, []);

  if (loading || !trialStatus || !trialStatus.isOnTrial || dismissed) {
    return null;
  }

  const isExpiringSoon = trialStatus.daysRemaining <= 3;
  const isExpired = trialStatus.trialExpired;

  if (isExpired) {
    return (
      <div className={`bg-danger-50 border-l-4 border-danger-400 relative ${outerClass} ${inMenu ? '' : 'mb-6'}`}>
        <div className={innerClass}>
          <div className="flex-shrink-0">
            <CreditCard className="h-5 w-5 text-danger-400" />
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-danger-700">
              <strong>Your trial has expired</strong> - Upgrade now to keep access to Investor features
            </p>
            <div className="mt-2">
              <button onClick={handleUpgrade} className="bg-danger-100 hover:bg-danger-200 text-danger-700 px-3 py-1 rounded text-sm font-medium cursor-pointer">
                Upgrade Now
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="ml-4 text-danger-400 hover:text-danger-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`border-l-4 relative ${outerClass} ${inMenu ? '' : 'mt-10 mb-0'} ${isExpiringSoon ? 'bg-investments-50 border-investments-400' : 'bg-authority-50 border-authority-400'}`}>
      <div className={innerClass}>
        <div className="flex-shrink-0">
          {isExpiringSoon ? (
            <Zap className="h-5 w-5 text-investments-400" />
          ) : (
            <Clock className="h-5 w-5 text-authority-400" />
          )}
        </div>
        <div className="ml-3 flex-1">
          <p className={`text-sm ${isExpiringSoon ? 'text-investments-700' : 'text-authority-700'}`}>
            <strong>
              {isExpiringSoon
                ? `Trial ending soon: ${trialStatus.daysRemaining} day${trialStatus.daysRemaining !== 1 ? 's' : ''} left`
                : `Free Trial Active: ${trialStatus.daysRemaining} days remaining`
              }
            </strong>
          </p>
          <p className={`text-sm mt-1 ${isExpiringSoon ? 'text-investments-600' : 'text-authority-600'}`}>
            {isExpiringSoon
              ? 'Upgrade now to keep access to all Investor features.'
              : `You have full access to all Investor features during your first ${SIGNUP_TRIAL_DAYS} days.`
            }
          </p>
          {isExpiringSoon && (
            <div className="mt-2">
              <button onClick={handleUpgrade} className="bg-investments-100 hover:bg-investments-200 text-investments-700 px-3 py-1 rounded text-sm font-medium cursor-pointer">
                Upgrade now
              </button>
            </div>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className={`ml-4 ${isExpiringSoon ? 'text-investments-400 hover:text-investments-600' : 'text-authority-400 hover:text-authority-600'}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default TrialBanner;
